import { List as ChakraList } from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { isDefined } from '~logic/helpers/isDefined'
import { BgColorProvider } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { cn } from '~ui/libs/classnames'

import { ListItem } from './ListItem'

type TProps<TItemType> = NoChildren &
  SkeletonProps & {
    /**
     * should the lines between items in the list be rendered?
     * _implemented as top borders of the item bellow the line_
     */
    withItemSeparator?: boolean
    /**
     * items that should be rendered in the list
     */
    items: RoA<TItemType>
    /**
     * if the list should have selectable items, this controls which one is selected
     */
    selectedItem?: TItemType
    /**
     * unique id if you need to access the selected list item from another place
     */
    selectedItemId?: PropsOf<typeof ChakraList>['id']
    /**
     * controls list’s overflow CSS property
     */
    overflow?: PropsOf<typeof ChakraList>['overflow']
    /**
     * if the list should have selectable items, this controls selected item bgColor
     */
    selectedBgColor?: TColorKey
    /**
     * key of the list item object that leads to some unique value for that item inside this list
     * used as a key and also for comparing selected item
     */
    idPropKey: keyof TItemType
    /**
     * called on list item click
     * also sets cursor to pointer if anything is passed
     */
    onItemClick?: (item: TItemType) => void
    /**
     * key of radius,
     * or an object with separate key for each corner
     * applied only to the selected item!
     */
    itemRadius?: PropsOf<typeof ListItem>['radius']
    /**
     * space inside/outside the item,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    itemSpacing?: PropsOf<typeof ListItem>['spacing']
    /**
     * function that should return rendered content that will be wrapped inside the ListItem
     */
    renderItem: (item: TItemType, isSelected?: boolean, index?: number) => React.ReactNode
  }

const SELECTED_LIST_ITEM_CN = `selected`

const StyledList = styled(ChakraList)`
  /* hide border around the selected item, but keep the same item height */
  .${SELECTED_LIST_ITEM_CN}, //
  .${SELECTED_LIST_ITEM_CN} + * {
    border-color: transparent;
  }
`

export const List = <TItemType,>({
  items,
  overflow,
  idPropKey,
  renderItem,
  itemRadius,
  itemSpacing,
  onItemClick,
  selectedItem,
  showSkeleton = false,
  selectedItemId,
  selectedBgColor,
  withItemSeparator,
}: TProps<TItemType>) => {
  return (
    <StyledList overflow={overflow}>
      {items.map((item, index) => {
        const hasSelectedItem = isDefined(selectedItem)
        const isSelectedItem = hasSelectedItem && item[idPropKey] === selectedItem[idPropKey]
        const itemBgColor = isSelectedItem ? selectedBgColor : null

        const isPreviousItemSelected =
          hasSelectedItem && index >= 1 && items[index - 1][idPropKey] === selectedItem[idPropKey]

        return (
          <Skeleton
            key={String(item[idPropKey] ?? index)}
            margin={{ vertical: `extraSmall`, horizontal: `none` }}
            showSkeleton={showSkeleton}
          >
            <BgColorProvider bgColor={itemBgColor ?? null}>
              <ListItem
                id={isSelectedItem ? selectedItemId : undefined}
                radius={itemRadius}
                spacing={itemSpacing}
                selected={isSelectedItem}
                className={cn({ [SELECTED_LIST_ITEM_CN]: isSelectedItem })}
                onClick={onItemClick ? () => onItemClick(item) : undefined}
                withBorderTop={index !== 0 && !isPreviousItemSelected && withItemSeparator}
              >
                {renderItem(item, isSelectedItem, index)}
              </ListItem>
            </BgColorProvider>
          </Skeleton>
        )
      })}
    </StyledList>
  )
}
